// src/pages/GameBoard.tsx

import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import { useNavigate, useLocation } from "react-router-dom";
import socket from "../socket";
import "../components/GameBoard.css";
import { motion } from "framer-motion";
import WinLoseOverlay from "../components/WinLoseOverlay";
import GameRatingModal from "../components/GameRatingModal";
import { Sound } from "../utils/sound";
import FasoorModal from "../components/FasoorModal";

import API_URL from "../config/api";

// =====================================================
// AVATAR URL
// =====================================================

const getAvatarUrl = (
  avatarPath: string | null | undefined
) => {
  if (!avatarPath) {
    return "/avatars/user.png";
  }

  // Uploaded avatar stored by backend
  if (avatarPath.startsWith("/uploads/")) {
    return `${API_URL}${avatarPath}`;
  }

  // Built-in frontend avatar
  return avatarPath;
};

// =====================================================
// TYPES
// =====================================================

interface Card {
  value: string;
  suit: string;
}

interface GameProfile {
  name?: string;
  avatar?: string;
  games_played?: number;
  wins?: number;
  losses?: number;
  draws?: number;
  rating?: number;
}

// =====================================================
// ROUND NAME
// =====================================================

const getRoundName = (round: number): string => {
  const roundNames: Record<number, string> = {
    1: "ONE",
    2: "TWO",
    3: "THREE",
    4: "FOUR",
    5: "FIVE",
    6: "SIX",
    7: "SEVEN",
    8: "EIGHT",
    9: "NINE",
    10: "TEN",
    11: "ELEVEN",
    12: "TWELVE",
    13: "THIRTEEN",
    14: "FOURTEEN",
    15: "FIFTEEN",
    16: "SIXTEEN",
    17: "SEVENTEEN",
    18: "EIGHTEEN",
    19: "NINETEEN",
    20: "TWENTY",
  };

  return roundNames[round] ?? String(round);
};

// =====================================================
// SOOR NAME
// =====================================================

const getSoorName = (
  count: number
): string => {

  const soorNames:
    Record<number, string> = {

    0: "NO SOOR",
    1: "ONE SOOR",
    2: "TWO SOORS",
    3: "THREE SOORS",
    4: "FOUR SOORS",
    5: "FIVE SOORS",
    6: "SIX SOORS",
    7: "SEVEN SOORS",
    8: "EIGHT SOORS",
    9: "NINE SOORS",
    10: "TEN SOORS",
  };

  return (
    soorNames[count] ??
    `${count} SOORS`
  );
};

// =====================================================
// GAME BOARD
// =====================================================

const GameBoard: React.FC = () => {
  const [hand, setHand] = useState<Card[]>([]);
  const [field, setField] = useState<Card[]>([]);
  const [collected, setCollected] = useState<Card[]>([]);
  const [yourTurn, setYourTurn] = useState(false);
  const [yourScore, setYourScore] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);

  // =====================================================
  // ACTIVE SOOR BADGES
  // =====================================================
  //
  // These are the server-authoritative UNMATCHED Soors
  // for the current 52-card round.
  //
  // Cancelled Soors disappear automatically when the
  // next gameStart state arrives.
  // =====================================================

  const [yourSoors, setYourSoors] =
    useState(0);

  const [opponentSoors, setOpponentSoors] =
    useState(0);

  // =====================================================
  // MOVE / CAPTURE PRESENTATION
  // =====================================================
  //
  // "field" remains the authoritative server field.
  //
  // visualField is used briefly when a player makes a
  // move so BOTH players can see which face-up card was
  // used before captured cards disappear.
  // =====================================================

  const [visualField, setVisualField] =
    useState<Card[] | null>(null);

  const moveAnimationRef =
    useRef(false);

  const moveAnimationTimerRef =
    useRef<ReturnType<typeof setTimeout> | null>(
      null
    );

  const pendingGameStateRef =
    useRef<any | null>(null);

  // =====================================================
  // MOBILE CARD PRESS
  // =====================================================

  const [pressedCardIndex, setPressedCardIndex] =
    useState<number | null>(null);

  // =====================================================
  // ROUND CINEMATIC
  // =====================================================

  const [roundNumber, setRoundNumber] = useState(1);
  const [showRoundIntro, setShowRoundIntro] = useState(false);
  const [roundLocked, setRoundLocked] = useState(false);

  // =====================================================
  // SOOR CINEMATIC
  // =====================================================

  const [showSoorIntro, setShowSoorIntro] =
    useState(false);

  const [soorCount, setSoorCount] =
    useState(0);

  const [soorCancelled, setSoorCancelled] =
    useState(false);

  const soorTimerRef =
    useRef<ReturnType<typeof setTimeout> | null>(
      null
    );

  // =====================================================
  // 17 POINT CLUB BONUS CINEMATIC
  // =====================================================

  const [showClubBonusIntro, setShowClubBonusIntro] =
    useState(false);

  const [clubBonusIsYou, setClubBonusIsYou] =
    useState(false);

  const clubBonusTimerRef =
    useRef<ReturnType<typeof setTimeout> | null>(
      null
    );


  // =====================================================
  // SWEET / COOL CINEMATIC
  // =====================================================
  //
  // Presentation only.
  //
  // COOL:
  // Successful Jack collection.
  //
  // SWEET:
  // Capture containing both an Ace and the 10♦.
  //
  // The backend remains authoritative for all scoring.
  // =====================================================

  const [specialReaction, setSpecialReaction] =
    useState<"cool" | "sweet" | null>(null);

  const specialReactionTimerRef =
    useRef<ReturnType<typeof setTimeout> | null>(
      null
    );


  const showSpecialReaction = (
    reaction: "cool" | "sweet"
  ) => {

    if (
      specialReactionTimerRef.current
    ) {

      window.clearTimeout(
        specialReactionTimerRef.current
      );

      specialReactionTimerRef.current =
        null;
    }


    setSpecialReaction(
      reaction
    );


    specialReactionTimerRef.current =
      window.setTimeout(
        () => {

          setSpecialReaction(
            null
          );

          specialReactionTimerRef.current =
            null;

        },
        2200
      );
  };

  /*
    Tracks the last round received from the server.

    This prevents the animation from replaying every
    time gameStart is emitted after a normal move.
  */
  const lastRoundRef = useRef<number | null>(null);

  const roundTimerRef =
    useRef<ReturnType<typeof setTimeout> | null>(null);

  const [remainingDeck, setRemainingDeck] = useState(52);
  const [gameEnded, setGameEnded] = useState(false);

  const [profile, setProfile] =
    useState<GameProfile | null>(null);

  const [opponentProfile, setOpponentProfile] =
    useState<GameProfile | null>(null);

  // =====================================================
  // PLAYER WIN RATE
  // =====================================================

  const getWinRate = (
    playerProfile: GameProfile | null
  ): number => {
    const games = Number(
      playerProfile?.games_played ?? 0
    );

    const wins = Number(
      playerProfile?.wins ?? 0
    );

    if (games <= 0) {
      return 0;
    }

    return Math.round(
      (wins / games) * 100
    );
  };

  const [showLeaveConfirm, setShowLeaveConfirm] =
    useState(false);

  const [showOpponentLeft, setShowOpponentLeft] =
    useState(false);

  const [gameResult, setGameResult] =
  useState<null | "win" | "lose">(null);

  // =====================================================
  // GAME RATING
  // =====================================================

  const [showGameRating, setShowGameRating] =
    useState(false);

  // =====================================================
  // REMATCH
  // =====================================================

  const [rematchWaiting, setRematchWaiting] =
    useState(false);

  const [showRematchRequest, setShowRematchRequest] =
    useState(false);

  const [showRematchDeclined, setShowRematchDeclined] =
    useState(false);

  const [showRematchUnavailable, setShowRematchUnavailable] =
    useState(false);

  const [rematchMessage, setRematchMessage] =
    useState("");

  const navigate = useNavigate();

  const location = useLocation();

  const roomCode =
    (location.state as { roomCode?: string })?.roomCode;

  // =====================================================
  // BACKGROUND SOUND
  // =====================================================

  useEffect(() => {
    Sound.playBackground();

    return () => {
      Sound.stopBackground();
    };
  }, []);

    // =====================================================
  // SOCKET EVENTS
  // =====================================================

  useEffect(() => {

    // =================================================
    // GAME STATE
    // =================================================

    const applyGameState = ({
      playerId,
      hand,
      field,
      yourTurn,
      yourScore,
      collected,
      remainingDeck,
      opponentScore,
      profile,
      opponentProfile,
      round,
      yourSoors,
      opponentSoors,
    }: any) => {

      // ===============================================
      // NORMAL GAME STATE
      // ===============================================

      setHand(
        hand ?? []
      );

      setField(
        field ?? []
      );

      setYourTurn(
        Boolean(yourTurn)
      );

      setCollected(
        collected ?? []
      );

      setRemainingDeck(
        remainingDeck ?? 0
      );

      setProfile(
        profile ?? null
      );

      setOpponentProfile(
        opponentProfile ?? null
      );


      // ===============================================
      // ACTIVE SOOR BADGES
      // ===============================================

      setYourSoors(
        Number(
          yourSoors ?? 0
        )
      );

      setOpponentSoors(
        Number(
          opponentSoors ?? 0
        )
      );


      // ===============================================
      // SCORES
      // ===============================================

      if (
        playerId === socket.id
      ) {

        setYourScore(
          yourScore ?? 0
        );

        setOpponentScore(
          opponentScore ?? 0
        );

      } else {

        setYourScore(
          opponentScore ?? 0
        );

        setOpponentScore(
          yourScore ?? 0
        );
      }


      // ===============================================
      // ROUND
      // ===============================================

      const incomingRound =
        Number(
          round ?? 1
        );

      setRoundNumber(
        incomingRound
      );


      /*
        IMPORTANT:

        gameStart may also be emitted after normal
        game-state changes.

        Therefore the cinematic and sound should only
        run when the server round number changes.
      */

      if (
        lastRoundRef.current !==
        incomingRound
      ) {

        lastRoundRef.current =
          incomingRound;


        // =============================================
        // ROUND SOUND
        // =============================================

        Sound.playRound();


        // =============================================
        // LOCK GAME DURING CINEMATIC
        // =============================================

        setRoundLocked(
          true
        );

        setShowRoundIntro(
          true
        );


        // Remove previous timer.
        if (
          roundTimerRef.current
        ) {

          clearTimeout(
            roundTimerRef.current
          );

          roundTimerRef.current =
            null;
        }


        // End cinematic.
        roundTimerRef.current =
          setTimeout(
            () => {

              setShowRoundIntro(
                false
              );

              setRoundLocked(
                false
              );

              roundTimerRef.current =
                null;

            },
            2100
          );
      }
    };


    // =================================================
    // GAME STATE RECEIVER
    // =================================================
    //
    // Normally gameStart is applied immediately.
    //
    // During a move animation, however, the server may
    // send the authoritative final state milliseconds
    // after moveResolved.
    //
    // Buffer that state until both players have had time
    // to SEE the played card on the table.
    // =================================================

    const handleGameStart = (
      data: any
    ) => {

      if (
        moveAnimationRef.current
      ) {

        pendingGameStateRef.current =
          data;

        return;
      }

      applyGameState(
        data
      );
    };


    // =================================================
    // MOVE RESOLVED
    // =================================================
    //
    // Server sends this event to the entire room.
    //
    // Therefore:
    //
    //   - you see the opponent's played card
    //   - opponent sees your played card
    //   - AI moves are also visible
    //
    // Sequence:
    //
    //   field before move
    //          +
    //      played card
    //          ↓
    //      visible pause
    //          ↓
    //     final field
    // =================================================

    const handleMoveResolved = (
      data: any
    ) => {

      if (
        !data ||
        !data.playedCard
      ) {
        return;
      }


      console.log(
        "🃏 MOVE RESOLVED:",
        data
      );


      // =================================================
      // CARD PLAY SOUND
      // =================================================
      //
      // moveResolved is broadcast for every valid move,
      // including human, opponent, and AI moves.
      //
      // Play exactly one card-placement sound here so
      // the local player's move is not sounded twice.
      // =================================================

      Sound.playCard();


      // =================================================
      // SUCCESSFUL JACK COLLECTION SOUND
      // =================================================
      //
      // A Jack gets the "Cool!" reaction only when the
      // backend confirms that the move captured cards.
      //
      // A Jack placed on an otherwise non-capturable
      // field does not play the reaction.
      // =================================================

      if (
        data.playedCard?.value === "Jack" &&
        data.captured === true
      ) {

        Sound.playCool();

        showSpecialReaction(
          "cool"
        );

      }


      // =================================================
      // ACE + 10♦ SPECIAL SOUND
      // =================================================
      //
      // The backend determines whether this capture
      // collected both an Ace and the 10♦.
      //
      // This is presentation only. The backend has
      // already calculated the authoritative score.
      // =================================================

      if (
        data.sweetBonus === true
      ) {

        Sound.playSweet();

        showSpecialReaction(
          "sweet"
        );

      }


      // Cancel any previous visual timer.
      if (
        moveAnimationTimerRef.current
      ) {

        clearTimeout(
          moveAnimationTimerRef.current
        );

        moveAnimationTimerRef.current =
          null;
      }


      // Lock authoritative field updates temporarily.
      moveAnimationRef.current =
        true;


      const fieldBefore:
        Card[] =
        Array.isArray(
          data.fieldBefore
        )
          ? data.fieldBefore
          : [];


      const playedCard:
        Card =
        data.playedCard;


      // =================================================
      // SHOW THE PLAYED CARD FACE-UP ON THE TABLE
      // =================================================
      //
      // Even when this move captures cards, the played
      // card is first added to the visible table.
      // =================================================

      setVisualField([
        ...fieldBefore,
        playedCard,
      ]);


      // =================================================
      // COLLECTION DELAY
      // =================================================
      //
      // Give both players enough time to recognize the
      // card that caused the capture.
      // =================================================

      moveAnimationTimerRef.current =
        setTimeout(
          () => {

            // =================================================
            // END VISUAL MOVE
            // =================================================
            //
            // Stop overriding the table and immediately restore
            // the authoritative state received from the server.
            //
            // This prevents an empty/stale visualField from
            // hiding the real cards on the table.
            // =================================================

            moveAnimationRef.current =
              false;


            if (
              pendingGameStateRef.current
            ) {

              const pendingState =
                pendingGameStateRef.current;

              pendingGameStateRef.current =
                null;

              applyGameState(
                pendingState
              );
            }


            setVisualField(
              null
            );


            moveAnimationTimerRef.current =
              null;

          },
          700
        );
    };


    // =================================================
    // SOOR EVENT
    // =================================================

    const handleSoorEvent = (
      data: any
    ) => {

      console.log(
        "🔥 SOOR EVENT RECEIVED:",
        data
      );


      if (!data) {
        return;
      }


      // ===============================================
      // CLEAR PREVIOUS SOOR TIMER
      // ===============================================

      if (
        soorTimerRef.current
      ) {

        clearTimeout(
          soorTimerRef.current
        );

        soorTimerRef.current =
          null;
      }


      // ===============================================
      // SOOR CANCELLED
      // ===============================================

      if (
        data.type ===
        "cancelled"
      ) {

        setSoorCancelled(
          true
        );

        setSoorCount(
          Number(
            data.remainingSoors ??
            0
          )
        );


        // Cancellation sound.
        Sound.playSoorCancelled();

      }

      // ===============================================
      // SOOR EARNED
      // ===============================================

      else if (
        data.type ===
        "earned"
      ) {

        setSoorCancelled(
          false
        );

        setSoorCount(
          Number(
            data.soorCount ??
            1
          )
        );


        // Soor sound.
        Sound.playSoor();

      }

      else {

        return;
      }


      // ===============================================
      // SHOW SOOR CINEMATIC
      // ===============================================

      setShowSoorIntro(
        true
      );


      soorTimerRef.current =
        setTimeout(
          () => {

            setShowSoorIntro(
              false
            );

            soorTimerRef.current =
              null;

          },
          2100
        );
    };


    // =================================================
    // 17 POINT CLUB BONUS EVENT
    // =================================================

    const handleClubBonusEvent = (
      data: any
    ) => {

      console.log(
        "♣ 17 POINT BONUS EVENT RECEIVED:",
        data
      );


      if (!data) {
        return;
      }


      // ===============================================
      // CLUB BONUS SOUND
      // ===============================================

      Sound.playClubBonus();


      // ===============================================
      // CLEAR PREVIOUS TIMER
      // ===============================================

      if (
        clubBonusTimerRef.current
      ) {

        clearTimeout(
          clubBonusTimerRef.current
        );

        clubBonusTimerRef.current =
          null;
      }


      // ===============================================
      // WHO RECEIVED THE BONUS?
      // ===============================================

      setClubBonusIsYou(
        data.playerId ===
        socket.id
      );


      // ===============================================
      // SHOW CINEMATIC
      // ===============================================

      setShowClubBonusIntro(
        true
      );


      clubBonusTimerRef.current =
        setTimeout(
          () => {

            setShowClubBonusIntro(
              false
            );

            clubBonusTimerRef.current =
              null;

          },
          2100
        );
    };


    // =================================================
    // GAME OVER
    // =================================================

    const handleGameOver = ({
      winner,
      profile: updatedProfile,
      opponentProfile: updatedOpponentProfile,
    }: any) => {

      // ===============================================
      // REFRESH SAVED PLAYER STATISTICS
      // ===============================================
      //
      // The backend sends these only after the match
      // result has been committed to PostgreSQL.
      // ===============================================

      if (updatedProfile) {
        setProfile(
          updatedProfile
        );
      }

      if (updatedOpponentProfile) {
        setOpponentProfile(
          updatedOpponentProfile
        );
      }


      setGameEnded(
        true
      );


      // Prevent any further moves.
      setRoundLocked(
        true
      );


      // Remove active cinematics.
      setShowRoundIntro(
        false
      );

      setShowSoorIntro(
        false
      );

      setShowClubBonusIntro(
        false
      );


      // ===============================================
      // CLEAR ROUND TIMER
      // ===============================================

      if (
        roundTimerRef.current
      ) {

        clearTimeout(
          roundTimerRef.current
        );

        roundTimerRef.current =
          null;
      }


      // ===============================================
      // CLEAR SOOR TIMER
      // ===============================================

      if (
        soorTimerRef.current
      ) {

        clearTimeout(
          soorTimerRef.current
        );

        soorTimerRef.current =
          null;
      }


      // ===============================================
      // CLEAR CLUB BONUS TIMER
      // ===============================================

      if (
        clubBonusTimerRef.current
      ) {

        clearTimeout(
          clubBonusTimerRef.current
        );

        clubBonusTimerRef.current =
          null;
      }


      // ===============================================
      // WIN / LOSE
      // ===============================================

      if (
        winner === socket.id
      ) {

        setGameResult(
          "win"
        );

        Sound.playWin();

      } else {

        setGameResult(
          "lose"
        );

        Sound.playLose();
      }
    };


    // =================================================
    // OPPONENT LEFT
    // =================================================

    const handlePlayerLeft = () => {

      setShowOpponentLeft(
        true
      );

      setRoundLocked(
        true
      );
    };


    // =================================================
    // REMATCH REQUESTED
    // =================================================

    const handleRematchRequested = (
      data: any
    ) => {

      console.log(
        "🔁 REMATCH REQUESTED:",
        data
      );

      setRematchWaiting(
        false
      );

      setShowRematchRequest(
        true
      );
    };


    // =================================================
    // REMATCH WAITING
    // =================================================

    const handleRematchWaiting = () => {

      setRematchWaiting(
        true
      );
    };


    // =================================================
    // REMATCH STARTED
    // =================================================

    const handleRematchStarted = (
      data: any
    ) => {

      console.log(
        "🔁 REMATCH STARTED:",
        data
      );


      // -----------------------------------------------
      // CLOSE OLD MATCH UI
      // -----------------------------------------------

      setGameResult(
        null
      );

      setShowGameRating(
        false
      );

      setShowRematchRequest(
        false
      );

      setShowRematchDeclined(
        false
      );

      setShowRematchUnavailable(
        false
      );

      setRematchWaiting(
        false
      );

      setRematchMessage(
        ""
      );


      // -----------------------------------------------
      // RESET GAME-END STATE
      // -----------------------------------------------

      setGameEnded(
        false
      );

      setRoundLocked(
        true
      );


      // -----------------------------------------------
      // RESET SCORES / ROUND PRESENTATION
      // -----------------------------------------------

      setYourScore(
        0
      );

      setOpponentScore(
        0
      );

      setYourSoors(
        0
      );

      setOpponentSoors(
        0
      );

      setRoundNumber(
        1
      );

      setRemainingDeck(
        52
      );


      // -----------------------------------------------
      // RESET CARDS
      // -----------------------------------------------

      setHand(
        []
      );

      setField(
        []
      );

      setCollected(
        []
      );

      setVisualField(
        null
      );


      // -----------------------------------------------
      // RESET CINEMATICS
      // -----------------------------------------------

      setShowRoundIntro(
        false
      );

      setShowSoorIntro(
        false
      );

      setShowClubBonusIntro(
        false
      );

      setSoorCount(
        0
      );

      setSoorCancelled(
        false
      );


      // -----------------------------------------------
      // RESET MOVE ANIMATION
      // -----------------------------------------------

      moveAnimationRef.current =
        false;

      pendingGameStateRef.current =
        null;


      if (
        moveAnimationTimerRef.current
      ) {

        clearTimeout(
          moveAnimationTimerRef.current
        );

        moveAnimationTimerRef.current =
          null;
      }


      // -----------------------------------------------
      // FORCE ROUND ONE CINEMATIC FOR NEW MATCH
      // -----------------------------------------------

      lastRoundRef.current =
        null;
    };


    // =================================================
    // REMATCH DECLINED
    // =================================================

    const handleRematchDeclined = (
      data: any
    ) => {

      setRematchWaiting(
        false
      );

      setRematchMessage(
        data?.message ||
        "Your opponent declined the rematch."
      );

      setShowRematchDeclined(
        true
      );
    };


    // =================================================
    // REMATCH UNAVAILABLE
    // =================================================

    const handleRematchUnavailable = (
      data: any
    ) => {

      setRematchWaiting(
        false
      );

      setRematchMessage(
        data?.message ||
        "Your opponent is no longer available."
      );

      setShowRematchUnavailable(
        true
      );
    };


    // =================================================
    // REMATCH ERROR
    // =================================================

    const handleRematchError = (
      data: any
    ) => {

      console.error(
        "❌ REMATCH ERROR:",
        data
      );

      setRematchWaiting(
        false
      );

      setRematchMessage(
        data?.message ||
        "The rematch could not be started."
      );

      setShowRematchUnavailable(
        true
      );
    };


    // =================================================
    // REGISTER SOCKET EVENTS
    // =================================================

    socket.on(
      "gameStart",
      handleGameStart
    );

    socket.on(
      "moveResolved",
      handleMoveResolved
    );

    socket.on(
      "soorEvent",
      handleSoorEvent
    );

    socket.on(
      "clubBonusEvent",
      handleClubBonusEvent
    );

    socket.on(
      "gameOver",
      handleGameOver
    );

    socket.on(
      "playerLeft",
      handlePlayerLeft
    );

    socket.on(
      "rematchRequested",
      handleRematchRequested
    );

    socket.on(
      "rematchWaiting",
      handleRematchWaiting
    );

    socket.on(
      "rematchStarted",
      handleRematchStarted
    );

    socket.on(
      "rematchDeclined",
      handleRematchDeclined
    );

    socket.on(
      "rematchUnavailable",
      handleRematchUnavailable
    );

    socket.on(
      "rematchError",
      handleRematchError
    );


    // =================================================
    // REQUEST INITIAL STATE
    // =================================================

    if (
      roomCode
    ) {

      socket.emit(
        "requestGameState",
        roomCode
      );
    }


    // =================================================
    // CLEANUP
    // =================================================

    return () => {

      // ===============================================
      // REMOVE SOCKET LISTENERS
      // ===============================================

      socket.off(
        "gameStart",
        handleGameStart
      );

      socket.off(
        "moveResolved",
        handleMoveResolved
      );

      socket.off(
        "soorEvent",
        handleSoorEvent
      );

      socket.off(
        "clubBonusEvent",
        handleClubBonusEvent
      );

      socket.off(
        "gameOver",
        handleGameOver
      );

      socket.off(
        "playerLeft",
        handlePlayerLeft
      );

      socket.off(
        "rematchRequested",
        handleRematchRequested
      );

      socket.off(
        "rematchWaiting",
        handleRematchWaiting
      );

      socket.off(
        "rematchStarted",
        handleRematchStarted
      );

      socket.off(
        "rematchDeclined",
        handleRematchDeclined
      );

      socket.off(
        "rematchUnavailable",
        handleRematchUnavailable
      );

      socket.off(
        "rematchError",
        handleRematchError
      );


      // ===============================================
      // CLEAR ROUND TIMER
      // ===============================================

      if (
        roundTimerRef.current
      ) {

        clearTimeout(
          roundTimerRef.current
        );

        roundTimerRef.current =
          null;
      }


      // ===============================================
      // CLEAR SOOR TIMER
      // ===============================================

      if (
        soorTimerRef.current
      ) {

        clearTimeout(
          soorTimerRef.current
        );

        soorTimerRef.current =
          null;
      }


      // ===============================================
      // CLEAR CLUB BONUS TIMER
      // ===============================================

      if (
        clubBonusTimerRef.current
      ) {

        clearTimeout(
          clubBonusTimerRef.current
        );

        clubBonusTimerRef.current =
          null;
      }


      if (
        moveAnimationTimerRef.current
      ) {

        clearTimeout(
          moveAnimationTimerRef.current
        );

        moveAnimationTimerRef.current =
          null;
      }

      moveAnimationRef.current =
        false;

      pendingGameStateRef.current =
        null;
    };

  }, [roomCode]);

  // =====================================================
  // ACTIONS
  // =====================================================

  const playCard = (
    card: Card
  ) => {
    /*
      Do not allow moves when:

      - It is not this player's turn
      - The game has ended
      - The round cinematic is running
      - The room is unavailable
    */

    if (
      !yourTurn ||
      gameEnded ||
      roundLocked ||
      showSoorIntro ||
      showClubBonusIntro ||
      !roomCode
    ) {
      return;
    }

    // Immediately lock the turn locally.
    setYourTurn(false);

    socket.emit(
      "sendMove",
      {
        roomId: roomCode,
        move: [card],
      }
    );
  };

  // =====================================================
  // LEAVE GAME
  // =====================================================

  const handleLeave = () => {
    setShowLeaveConfirm(true);
  };

  const cancelLeave = () => {
    setShowLeaveConfirm(false);
  };

  const confirmLeave = () => {
    setShowLeaveConfirm(false);

    socket.emit(
      "leaveRoom",
      {
        roomId: roomCode,
      }
    );

    navigate("/");
  };

  // =====================================================
  // OPPONENT LEFT
  // =====================================================

  const handleOpponentLeftConfirm = () => {
    setShowOpponentLeft(false);
    navigate("/");
  };

  // =====================================================
  // REQUEST REMATCH
  // =====================================================

  const handleRematch = () => {

    if (
      !roomCode ||
      rematchWaiting
    ) {
      return;
    }


    setRematchWaiting(
      true
    );

    setShowRematchDeclined(
      false
    );

    setShowRematchUnavailable(
      false
    );

    setRematchMessage(
      ""
    );


    socket.emit(
      "requestRematch"
    );
  };


  // =====================================================
  // ACCEPT REMATCH
  // =====================================================

  const handleAcceptRematch = () => {

    setShowRematchRequest(
      false
    );

    setRematchWaiting(
      true
    );


    socket.emit(
      "acceptRematch"
    );
  };


  // =====================================================
  // DECLINE REMATCH
  // =====================================================

  const handleDeclineRematch = () => {

    setShowRematchRequest(
      false
    );


    socket.emit(
      "declineRematch"
    );
  };


  // =====================================================
  // CLOSE REMATCH MESSAGE
  // =====================================================

  const handleCloseRematchMessage = () => {

    setShowRematchDeclined(
      false
    );

    setShowRematchUnavailable(
      false
    );

    setRematchMessage(
      ""
    );
  };


  // =====================================================
  // RESULT → RATING
  // =====================================================

  const handleResultContinue = () => {

    /*
      The match was genuinely completed.

      Back to Game House keeps the existing rating
      experience before returning home.
    */

    setRematchWaiting(
      false
    );

    setShowRematchRequest(
      false
    );

    setGameResult(
      null
    );

    setShowGameRating(
      true
    );
  };


  // =====================================================
  // RATING → GAME HOUSE
  // =====================================================

  const handleRatingComplete = () => {

    setShowGameRating(
      false
    );


    /*
      Explicitly leave the completed Socket.IO game
      before navigating away.

      This clears the server-side game, player mapping,
      and any pending rematch request.
    */

    socket.emit(
      "leaveRoom"
    );


    navigate(
      "/"
    );
  };

  // =====================================================
  // CARD RENDER
  // =====================================================

  const renderCard = (
    card: Card,
    onClick?: () => void
  ) => {
    const suitMap: Record<string, string> = {
      "♠": "spades",
      "♥": "hearts",
      "♦": "diamonds",
      "♣": "clubs",
      spades: "spades",
      hearts: "hearts",
      diamonds: "diamonds",
      clubs: "clubs",
    };

    const valueMap: Record<string, string> = {
      Ace: "ace",
      "2": "2",
      "3": "3",
      "4": "4",
      "5": "5",
      "6": "6",
      "7": "7",
      "8": "8",
      "9": "9",
      "10": "10",
      Jack: "jack",
      Queen: "queen",
      King: "king",
    };

    const suit =
      suitMap[card.suit] ||
      card.suit.toLowerCase();

    const value =
      valueMap[card.value] ||
      card.value.toLowerCase();

    const imagePath =
      `/cards/${value}_of_${suit}.png`;

    return (
      <motion.img
        src={imagePath}
        alt={`${card.value} of ${card.suit}`}
        className={`playing-card ${onClick ? "clickable" : ""}`}
        onClick={onClick}
        draggable={false}
        initial={{
          scale: 0.5,
          opacity: 0,
          y: -200,
        }}
        animate={{
          scale: 1,
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.4,
          ease: "easeOut",
        }}
      />
    );
  };

  if (!roomCode) {
    return (
      <div>
        Loading game...
      </div>
    );
  }

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="game-container">

      {/* ============================================= */}
      {/* PREMIUM ROUND CINEMATIC                       */}
      {/* ============================================= */}

      {showRoundIntro && (
        <div
          className="round-cinematic"
          role="status"
          aria-live="polite"
          aria-label={`Round ${roundNumber}`}
        >
          {/* AMBIENT GOLD GLOW */}
          <div
            className="round-ambient-glow"
            aria-hidden="true"
          />

          {/* DECORATIVE RINGS */}
          <div
            className="round-ring round-ring-outer"
            aria-hidden="true"
          />

          <div
            className="round-ring round-ring-inner"
            aria-hidden="true"
          />

          {/* SIDE ORNAMENTS */}
          <div
            className="round-ornament round-ornament-left"
            aria-hidden="true"
          />

          <div
            className="round-ornament round-ornament-right"
            aria-hidden="true"
          />

          {/* ROUND TITLE */}
          <div className="round-cinematic-content">
            <div className="round-eyebrow">
              ROUND
            </div>

            <div className="round-word">
              {getRoundName(roundNumber)}
            </div>

            <div className="round-divider">
              <span />

              <strong>
                ◆
              </strong>

              <span />
            </div>

            <div className="round-game-name">
              FASOOR
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* TOP BAR                                       */}
      {/* ============================================= */}

      {/* ============================================= */}
      {/* PREMIUM SOOR CINEMATIC                        */}
      {/* ============================================= */}

      {showSoorIntro && (
        <div
          className={
            `round-cinematic soor-cinematic ${
              soorCancelled
                ? "soor-cinematic-cancelled"
                : "soor-cinematic-earned"
            }`
          }
          role="status"
          aria-live="assertive"
          aria-label={
            soorCancelled
              ? "Soor cancelled"
              : getSoorName(
                  soorCount
                )
          }
        >

          {/* AMBIENT GOLD GLOW */}

          <div
            className="round-ambient-glow"
            aria-hidden="true"
          />


          {/* DECORATIVE RINGS */}

          <div
            className="
              round-ring
              round-ring-outer
            "
            aria-hidden="true"
          />

          <div
            className="
              round-ring
              round-ring-inner
            "
            aria-hidden="true"
          />


          {/* SIDE ORNAMENTS */}

          <div
            className="
              round-ornament
              round-ornament-left
            "
            aria-hidden="true"
          />

          <div
            className="
              round-ornament
              round-ornament-right
            "
            aria-hidden="true"
          />


          {/* SOOR TITLE */}

          <div
            className="
              round-cinematic-content
              soor-cinematic-content
            "
          >

            <div
              className="round-eyebrow"
            >
              SOOR
            </div>


            <div
              className={
                `round-word soor-word ${
                  soorCancelled
                    ? "soor-word-cancelled"
                    : ""
                }`
              }
            >

              {soorCancelled
                ? "CANCELLED"
                : getSoorName(
                    soorCount
                  )
                    .replace(
                      " SOORS",
                      ""
                    )
                    .replace(
                      " SOOR",
                      ""
                    )}

            </div>


            <div
              className="round-divider"
            >

              <span />

              <strong>
                ◆
              </strong>

              <span />

            </div>


            <div
              className="round-game-name"
            >

              {soorCancelled
                ? "SOORS CANCEL EACH OTHER"
                : getSoorName(
                    soorCount
                  )}

            </div>

          </div>

        </div>
      )}

      {/* ============================================= */}
      {/* SWEET / COOL SPECIAL REACTION CINEMATIC       */}
      {/* ============================================= */}

      {specialReaction && (

        <div
          className={`
            round-cinematic
            special-reaction-cinematic
            special-reaction-${specialReaction}
          `}
          role="status"
          aria-live="assertive"
          aria-label={
            specialReaction === "sweet"
              ? "Sweet"
              : "Cool"
          }
        >

          {/* AMBIENT GLOW */}

          <div
            className="round-ambient-glow"
            aria-hidden="true"
          />


          {/* DECORATIVE RINGS */}

          <div
            className="
              round-ring
              round-ring-outer
            "
            aria-hidden="true"
          />

          <div
            className="
              round-ring
              round-ring-inner
            "
            aria-hidden="true"
          />


          {/* SIDE ORNAMENTS */}

          <div
            className="
              round-ornament
              round-ornament-left
            "
            aria-hidden="true"
          />

          <div
            className="
              round-ornament
              round-ornament-right
            "
            aria-hidden="true"
          />


          {/* REACTION CONTENT */}

          <div
            className="
              round-cinematic-content
              special-reaction-content
            "
          >

            <div className="round-eyebrow">

              {specialReaction === "sweet"
                ? "SPECIAL CAPTURE"
                : "JACK CAPTURE"}

            </div>


            <div
              className="
                special-reaction-symbol
              "
              aria-hidden="true"
            >

              {specialReaction === "sweet"
                ? "♦"
                : "♠"}

            </div>


            <div
              className="
                round-word
                special-reaction-word
              "
            >

              {specialReaction === "sweet"
                ? "SWEET!"
                : "COOL!"}

            </div>


            <div className="round-divider">

              <span />

              <strong>
                ◆
              </strong>

              <span />

            </div>


            <div className="round-game-name">

              {specialReaction === "sweet"
                ? "ACE + 10♦"
                : "NICE CAPTURE"}

            </div>

          </div>

        </div>

      )}


      {/* ============================================= */}
      {/* PREMIUM 17 POINT CLUB BONUS CINEMATIC         */}
      {/* ============================================= */}

      {showClubBonusIntro && (
        <div
          className="
            round-cinematic
            club-bonus-cinematic
          "
          role="status"
          aria-live="assertive"
          aria-label="17 point clubs bonus"
        >

          {/* AMBIENT GOLD GLOW */}

          <div
            className="round-ambient-glow"
            aria-hidden="true"
          />


          {/* DECORATIVE RINGS */}

          <div
            className="
              round-ring
              round-ring-outer
            "
            aria-hidden="true"
          />

          <div
            className="
              round-ring
              round-ring-inner
            "
            aria-hidden="true"
          />


          {/* SIDE ORNAMENTS */}

          <div
            className="
              round-ornament
              round-ornament-left
            "
            aria-hidden="true"
          />

          <div
            className="
              round-ornament
              round-ornament-right
            "
            aria-hidden="true"
          />


          {/* 17 POINT BONUS TITLE */}

          <div
            className="
              round-cinematic-content
              club-bonus-cinematic-content
            "
          >

            <div className="round-eyebrow">
              ♣ CLUB BONUS ♣
            </div>


            <div
              className="
                round-word
                club-bonus-word
              "
            >
              17
            </div>


            <div className="round-divider">

              <span />

              <strong>
                ◆
              </strong>

              <span />

            </div>


            <div className="round-game-name">

              {clubBonusIsYou
                ? "YOU EARNED 17 POINTS"
                : "OPPONENT EARNED 17 POINTS"}

            </div>

          </div>

        </div>
      )}

      <div className="top-bar">

        {/* =========================================== */}
        {/* LEFT SIDE                                   */}
        {/* Logo + Game HUD                             */}
        {/* =========================================== */}

        <div className="left-panel">

          {/* ========================================= */}
          {/* LOGO                                      */}
          {/* ========================================= */}

          <div className="logo">
            ♠ P-ARENA
          </div>


          {/* ========================================= */}
          {/* GAME HUD                                  */}
          {/* Deck • Collected • Round • Turn • Leave  */}
          {/* ========================================= */}

          <div className="game-hud">

            {/* ======================================= */}
            {/* GAME STATS                              */}
            {/* ======================================= */}

            <div className="game-stats">

              <div className="stat">
                <span>Deck:</span>
                {remainingDeck}
              </div>

              <div className="stat">
                <span>Collected:</span>
                {collected.length}
              </div>

              <div className="stat">
                <span>Round:</span>
                {roundNumber}
              </div>

            </div>


            {/* ======================================= */}
            {/* TURN                                    */}
            {/* ======================================= */}

            <div
              className={`turn ${
                yourTurn && !roundLocked
                  ? "your-turn"
                  : ""
              }`}
            >
              {roundLocked ? (
                <>
                  ♠ ROUND STARTING
                </>
              ) : yourTurn ? (
                <>
                  🟢 YOUR TURN
                </>
              ) : (
                <>
                  ⏳ WAIT
                </>
              )}
            </div>


            {/* ======================================= */}
            {/* LEAVE GAME                              */}
            {/* ======================================= */}

            <button
              type="button"
              className="leave-btn"
              onClick={handleLeave}
            >
              🚪 Leave Game
            </button>

          </div>

        </div>


        {/* =========================================== */}
        {/* ROOM                                        */}
        {/* =========================================== */}

        <div className="room">
          Room {roomCode}
        </div>

      </div>
      {/* ============================================= */}
      {/* OPPONENT                                      */}
      {/* ============================================= */}

      <div className="opponent-area">

        {/* OPPONENT PROFILE CARD */}
        <div className="game-player-card opponent-player-card">
          <div className="game-player-identity">
            <div className="game-player-avatar-shell">
              <img
                src={getAvatarUrl(opponentProfile?.avatar)}
                alt={opponentProfile?.name || "Opponent"}
                className="game-player-avatar"
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = "/avatars/user.png";
                }}
              />

              <span
                className="game-player-status"
                aria-label="Online"
              />
            </div>

            <div className="game-player-heading">
              <div className="game-player-label">
                OPPONENT
              </div>

              <div className="game-player-name">
                {opponentProfile?.name || "Opponent"}
              </div>

              {opponentSoors > 0 && (
                <div
                  className="soor-badge"
                  aria-label={`${opponentSoors} active Soor${opponentSoors === 1 ? "" : "s"}`}
                >
                  <span className="soor-badge-label">
                    SOOR
                  </span>

                  <strong>
                    × {opponentSoors}
                  </strong>
                </div>
              )}
            </div>
          </div>

          {/* OPPONENT STATS */}
          <div className="game-player-stats">
            <div className="game-player-stat score-stat">
              <strong>{opponentScore}</strong>
              <span>Score</span>
            </div>

            <div className="game-player-stat">
              <strong>
                {opponentProfile?.games_played ?? 0}
              </strong>
              <span>Games</span>
            </div>

            <div className="game-player-stat">
              <strong>
                {opponentProfile?.wins ?? 0}
              </strong>
              <span>Wins</span>
            </div>

            <div className="game-player-stat">
              <strong>
                {opponentProfile?.losses ?? 0}
              </strong>
              <span>Losses</span>
            </div>

            <div className="game-player-stat win-rate-stat">
              <strong>
                {getWinRate(opponentProfile)}%
              </strong>
              <span>Win Rate</span>
            </div>
          </div>
        </div>

        {/* OPPONENT HAND */}
        <div className="opponent-hand">
          {Array(hand.length)
            .fill(0)
            .map((_, i) => (
              <img
                key={i}
                src="/cards/back.png"
                className="card-back"
                alt="card back"
              />
            ))}
        </div>
      </div>

      {/* ============================================= */}
      {/* TABLE                                         */}
      {/* ============================================= */}

      <div className="table-area">
        {(visualField ?? field).length > 0 ? (
          (visualField ?? field).map(
            (card, index) => (
              <React.Fragment
                key={`${card.value}-${card.suit}-${index}`}
              >
                {renderCard(card)}
              </React.Fragment>
            )
          )
        ) : (
          <p className="empty-text">
            No cards on table
          </p>
        )}
      </div>

      {/* ============================================= */}
      {/* PLAYER                                        */}
      {/* ============================================= */}

      <div className="player-area">

        {/* =========================================== */}
        {/* PLAYER HAND                                 */}
        {/* =========================================== */}

        <div className="player-hand">
          {hand.length > 0 ? (
            hand.map((card, index) => {
              const total = hand.length;
              const middle = (total - 1) / 2;
              const offset = index - middle;

              // Adaptive spacing
              const spacing =
                Math.max(-35, -300 / total);

              // Adaptive rotation
              const rotation =
                offset * (total > 6 ? 4 : 6);

              const isPressed =
                pressedCardIndex === index;

              return (
                <div
                  key={`${card.value}-${card.suit}-${index}`}
                  className="card-wrapper"
                  style={{
                    marginLeft:
                      index === 0
                        ? 0
                        : `${spacing}px`,

                    /*
                      IMPORTANT:
                      Keep the fan transform here.
                      Mobile press animation happens
                      on the inner wrapper instead.
                    */
                    transform:
                      `rotate(${rotation}deg) translateY(${Math.abs(offset) * 5}px)`,

                    zIndex:
                      isPressed
                        ? 999
                        : index,
                  }}
                >
                  <div
                    className={
                      `card-touch-wrapper ${
                        isPressed
                          ? "card-touch-wrapper--pressed"
                          : ""
                      }`
                    }

                    onPointerDown={() => {
                      if (
                        yourTurn &&
                        !gameEnded &&
                        !roundLocked &&
                        !showSoorIntro &&
                        !showClubBonusIntro
                      ) {
                        setPressedCardIndex(index);
                      }
                    }}

                    onPointerUp={() => {
                      setPressedCardIndex(null);
                    }}

                    onPointerCancel={() => {
                      setPressedCardIndex(null);
                    }}

                    onPointerLeave={() => {
                      setPressedCardIndex(null);
                    }}
                  >
                    {renderCard(
                      card,
                      () => {
                        setPressedCardIndex(null);
                        playCard(card);
                      }
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p>
              No cards
            </p>
          )}
        </div>

        {/* =========================================== */}
        {/* PLAYER PROFILE CARD                         */}
        {/* BELOW THE PLAYER HAND                       */}
        {/* =========================================== */}

        <div className="game-player-card your-player-card">
          <div className="game-player-identity">
            <div className="game-player-avatar-shell">
              <img
                src={getAvatarUrl(profile?.avatar)}
                alt={profile?.name || "You"}
                className="game-player-avatar"
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = "/avatars/user.png";
                }}
              />

              <span
                className="game-player-status"
                aria-label="Online"
              />
            </div>

            <div className="game-player-heading">
              <div className="game-player-label">
                YOU
              </div>

              <div className="game-player-name">
                {profile?.name || "You"}
              </div>

              {yourSoors > 0 && (
                <div
                  className="soor-badge"
                  aria-label={`${yourSoors} active Soor${yourSoors === 1 ? "" : "s"}`}
                >
                  <span className="soor-badge-label">
                    SOOR
                  </span>

                  <strong>
                    × {yourSoors}
                  </strong>
                </div>
              )}
            </div>
          </div>

          {/* PLAYER STATS */}
          <div className="game-player-stats">
            <div className="game-player-stat score-stat">
              <strong>{yourScore}</strong>
              <span>Score</span>
            </div>

            <div className="game-player-stat">
              <strong>
                {profile?.games_played ?? 0}
              </strong>
              <span>Games</span>
            </div>

            <div className="game-player-stat">
              <strong>
                {profile?.wins ?? 0}
              </strong>
              <span>Wins</span>
            </div>

            <div className="game-player-stat">
              <strong>
                {profile?.losses ?? 0}
              </strong>
              <span>Losses</span>
            </div>

            <div className="game-player-stat win-rate-stat">
              <strong>
                {getWinRate(profile)}%
              </strong>
              <span>Win Rate</span>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================= */}
      {/* LEAVE GAME MODAL                              */}
      {/* ============================================= */}

      <FasoorModal
        open={showLeaveConfirm}
        type="danger"
        title="Leave Game?"
        message="Are you sure you want to leave this game?"
        description="Your current game will end and your opponent will be notified."
        confirmText="🚪 Leave Game"
        cancelText="Stay in Game"
        showCancel
        onCancel={cancelLeave}
        onConfirm={confirmLeave}
      />

      {/* ============================================= */}
      {/* OPPONENT LEFT MODAL                           */}
      {/* ============================================= */}

      <FasoorModal
        open={showOpponentLeft}
        type="warning"
        title="Opponent Left"
        message="Your opponent has left the game."
        description="This game has ended. You can return to the Game House and start another game."
        confirmText="Return to Game House"
        onConfirm={handleOpponentLeftConfirm}
      />

      {/* ============================================= */}
      {/* INCOMING REMATCH REQUEST                      */}
      {/* ============================================= */}

      <FasoorModal
        open={
          showRematchRequest
        }
        type="success"
        title="Rematch?"
        message="Your opponent wants to play again."
        description="Accept to start a completely new match with scores reset to 0–0."
        confirmText="🔁 Accept Rematch"
        cancelText="Decline"
        showCancel
        onCancel={
          handleDeclineRematch
        }
        onConfirm={
          handleAcceptRematch
        }
      />


      {/* ============================================= */}
      {/* REMATCH DECLINED                              */}
      {/* ============================================= */}

      <FasoorModal
        open={
          showRematchDeclined
        }
        type="warning"
        title="Rematch Declined"
        message={
          rematchMessage ||
          "Your opponent declined the rematch."
        }
        description="You can return to the completed game screen and choose Back to Game House."
        confirmText="OK"
        onConfirm={
          handleCloseRematchMessage
        }
      />


      {/* ============================================= */}
      {/* REMATCH UNAVAILABLE / ERROR                   */}
      {/* ============================================= */}

      <FasoorModal
        open={
          showRematchUnavailable
        }
        type="warning"
        title="Rematch Unavailable"
        message={
          rematchMessage ||
          "The rematch could not be started."
        }
        description="You can return to the Game House and start another game."
        confirmText="OK"
        onConfirm={
          handleCloseRematchMessage
        }
      />


      {/* ============================================= */}
      {/* WIN / LOSE OVERLAY                            */}
      {/* ============================================= */}

      {gameResult && !showRematchRequest && (
        <WinLoseOverlay
          isWinner={
            gameResult === "win"
          }
          onRematch={
            handleRematch
          }
          onBackToGameHouse={
            handleResultContinue
          }
          rematchWaiting={
            rematchWaiting
          }
        />
      )}

      {/* ============================================= */}
      {/* PREMIUM GAME RATING                           */}
      {/* ============================================= */}

      <GameRatingModal
        open={
          showGameRating
        }
        gameKey="fasoor"
        gameName="Fasoor"
        onComplete={
          handleRatingComplete
        }
      />
    </div>
  );
};

export default GameBoard;